import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { heroImagesQuery } from "@/lib/publicQueries";

export interface HeroImage {
  id: string;
  page_identifier: string;
  image_url: string;
  image_type: 'upload' | 'portfolio';
  project_id: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export const useHeroImages = () => {
  return useQuery(heroImagesQuery());
};

export const useHeroImage = (pageIdentifier: string) => {
  return useQuery({
    queryKey: ["hero-image", pageIdentifier],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("page_hero_images")
        .select("*")
        .eq("page_identifier", pageIdentifier)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data as HeroImage | null;
    },
  });
};

export const useUpdateHeroImage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      pageIdentifier,
      imageUrl,
      imageType,
      projectId,
    }: {
      pageIdentifier: string;
      imageUrl: string;
      imageType: 'upload' | 'portfolio';
      projectId?: string | null;
    }) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session?.user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("page_hero_images")
        .upsert({
          page_identifier: pageIdentifier,
          image_url: imageUrl,
          image_type: imageType,
          project_id: projectId || null,
          updated_by: session.session.user.id,
        }, {
          onConflict: 'page_identifier'
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hero-images"] });
      queryClient.invalidateQueries({ queryKey: ["hero-image"] });
      toast.success("Hero image updated successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to update hero image: ${error.message}`);
    },
  });
};
